import { SECTION_META, menuPages, type PageNode, type SectionType, type WebsiteDefinition } from "@baze/schema";
import { safeHref } from "./helpers";

export interface NavLink {
  label: string;
  href: string;
  /** True for the page currently being rendered (multi-page sites). */
  current?: boolean;
}

const NON_NAV: ReadonlySet<SectionType> = new Set(["navbar", "footer"]);

/**
 * Navigation is derived, never hand-written:
 * - more than one page in the menu → one link per menu page (slug); pages with `hideFromNav` are left out;
 * - otherwise → sections of the home page with `showInNav`, linking to `#anchor` (`/#anchor` from
 *   another page), labelled navLabel, falling back to the section type label.
 */
export function derivedNav(definition: WebsiteDefinition, page: PageNode | undefined): NavLink[] {
  const pages = menuPages(definition);
  if (pages.length > 1) {
    return pages.flatMap((p) => {
      const href = safeHref(p.slug);
      return href ? [{ label: p.title, href, current: p.id === page?.id }] : [];
    });
  }
  // One-page menu: the home page's sections, also on pages kept out of the menu.
  const home = pages[0];
  if (!home) return [];
  const prefix = page && page.id !== home.id ? "/" : "";
  return home.sections.flatMap((s) => {
    if (s.showInNav !== true || s.hidden || NON_NAV.has(s.type)) return [];
    const href = safeHref(`${prefix}#${s.anchor}`);
    const label = s.navLabel?.trim() || SECTION_META[s.type]?.label || s.type;
    return href ? [{ label, href }] : [];
  });
}
